import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { adminAPI } from '../../api/client'
import { publicAssetUrl } from '../../utils/assetUrl'
import { conditionLabel } from '../../utils/conditionLabel'
import { listingPublicPath } from '../../utils/listingPath'
import { useAdminUi } from './AdminUiProvider'
import type { AdminListingRow } from './adminTypes'
import { formatWhen } from './adminUtils'

type Props = {
  token: string
  listingId: number
  onClose: () => void
  onUpdated: (listing: AdminListingRow) => void
  onOpenUser?: (userId: number) => void
}

export default function AdminListingDrawer({
  token,
  listingId,
  onClose,
  onUpdated,
  onOpenUser,
}: Props) {
  const { t, i18n } = useTranslation()
  const { toast } = useAdminUi()
  const [listing, setListing] = useState<AdminListingRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [photoIndex, setPhotoIndex] = useState(0)

  const load = useCallback(() => {
    setLoading(true)
    setError('')
    adminAPI
      .getListing(token, listingId)
      .then((res) => {
        setListing(res.data as AdminListingRow)
        setPhotoIndex(0)
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false))
  }, [token, listingId])

  useEffect(() => {
    load()
  }, [load])

  function patch(body: { is_hidden?: boolean; is_flagged_suspicious?: boolean }) {
    setBusy(true)
    adminAPI
      .updateListing(token, listingId, body)
      .then((res) => {
        const updated = res.data as AdminListingRow
        setListing(updated)
        onUpdated(updated)
        toast.success(t('admin.toast.listingUpdated'))
      })
      .catch((err) => toast.error(err.response?.data?.detail || err.message))
      .finally(() => setBusy(false))
  }

  return (
    <div className="vc-admin-drawer" role="dialog" aria-modal="true">
      <button type="button" className="vc-admin-drawer__backdrop" onClick={onClose} aria-label="Close" />
      <aside className="vc-admin-drawer__panel vc-admin-drawer__panel--listing">
        <div className="vc-admin-listing-sheet">
          <header className="vc-admin-listing-sheet__head">
            <div>
              <p className="vc-admin-listing-sheet__kicker">
                {[listing?.manufacturer, listing?.model_name].filter(Boolean).join(' ') || t('admin.listing')}
              </p>
              <h2>{listing?.title ?? t('admin.listing')}</h2>
            </div>
            <button type="button" className="vc-admin-drawer__close" onClick={onClose}>
              ×
            </button>
          </header>

          {error ? <p className="vc-admin-error">{error}</p> : null}
          {loading ? <p className="meta-text">{t('common.loading')}</p> : null}

          {listing ? (
            <>
              <div className="vc-admin-listing-price">
                <div>
                  <strong>€{listing.price.toFixed(0)}</strong>
                  {listing.seller_city ? <span>{listing.seller_city}</span> : null}
                </div>
                {listing.ai_recommended_eur != null ? (
                  <div
                    className={`vc-admin-listing-ai${
                      listing.price > listing.ai_recommended_eur * 1.25 ? ' is-high' : ''
                    }`}
                  >
                    <span>{t('admin.aiRecommended')}</span>
                    <b>€{listing.ai_recommended_eur.toFixed(0)}</b>
                  </div>
                ) : null}
              </div>

              <div className="vc-admin-listing-pills">
                <span className={listing.is_sold ? 'is-sold' : 'is-live'}>
                  {listing.is_sold ? t('seller.sold') : t('admin.statusActive')}
                </span>
                {listing.is_hidden ? <span className="is-warn">{t('admin.hidden')}</span> : null}
                {listing.is_flagged_suspicious ? <span className="is-flag">{t('admin.flagged')}</span> : null}
                {listing.condition ? <span>{conditionLabel(listing.condition, t)}</span> : null}
              </div>

              {listing.images && listing.images.length > 0 ? (
                <div className="vc-admin-listing-gallery">
                  <a
                    className="vc-admin-listing-stage"
                    href={publicAssetUrl(listing.images[photoIndex]?.display_url || listing.images[0].display_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={publicAssetUrl(
                        listing.images[photoIndex]?.display_url || listing.images[0].display_url,
                      )}
                      alt=""
                    />
                  </a>
                  {listing.images.length > 1 ? (
                    <div className="vc-admin-listing-film">
                      {listing.images.map((img, index) => (
                        <button
                          key={img.id}
                          type="button"
                          className={index === photoIndex ? 'is-active' : undefined}
                          onClick={() => setPhotoIndex(index)}
                        >
                          <img src={publicAssetUrl(img.thumbnail_url || img.display_url)} alt="" />
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <button
                type="button"
                className="vc-admin-listing-owner"
                onClick={() => onOpenUser?.(listing.owner_id)}
              >
                <span aria-hidden="true">
                  {(listing.owner_full_name || listing.owner_email || '?').slice(0, 1).toUpperCase()}
                </span>
                <span>
                  <b>{listing.owner_full_name || listing.owner_email}</b>
                  {listing.owner_full_name && listing.owner_email ? <small>{listing.owner_email}</small> : null}
                </span>
              </button>

              <dl className="vc-admin-listing-facts">
                <div>
                  <dt>{t('admin.photos')}</dt>
                  <dd>{listing.photos_count}</dd>
                </div>
                <div>
                  <dt>{t('admin.created')}</dt>
                  <dd>{formatWhen(listing.created_at, i18n.language)}</dd>
                </div>
              </dl>
            </>
          ) : null}
        </div>

        {listing ? (
          <footer className="vc-admin-listing-sheet__foot">
            <button
              type="button"
              className="vc-admin-btn vc-admin-btn--primary"
              onClick={() =>
                window.open(listingPublicPath(listing.id, i18n.language), '_blank', 'noopener,noreferrer')
              }
            >
              {t('admin.viewListing')}
            </button>
            <div className="vc-admin-listing-sheet__pair">
              <button
                type="button"
                className="vc-admin-btn vc-admin-btn--ghost"
                disabled={busy}
                onClick={() => patch({ is_hidden: !listing.is_hidden })}
              >
                {listing.is_hidden ? t('admin.unhideListing') : t('admin.hideListing')}
              </button>
              <button
                type="button"
                className={`vc-admin-btn${listing.is_flagged_suspicious ? ' vc-admin-btn--primary' : ' vc-admin-btn--danger'}`}
                disabled={busy}
                onClick={() => patch({ is_flagged_suspicious: !listing.is_flagged_suspicious })}
              >
                {listing.is_flagged_suspicious ? t('admin.unflagListing') : t('admin.flagListing')}
              </button>
            </div>
          </footer>
        ) : null}
      </aside>
    </div>
  )
}
